import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useMemo } from 'react'

import { api } from '@/lib/api'
import type {
  McpAgentToolBinding,
  McpConnection,
  McpConnectionInput,
  McpConnectionUpdateInput,
  McpTool,
  McpToolConfirmation,
} from '@/types/mcp'

export const mcpKeys = {
  connections: (orgId: string | null) => ['mcp', 'connections', orgId] as const,
  tools: (orgId: string | null, connectionId: string | null) =>
    ['mcp', 'tools', orgId, connectionId] as const,
  agentBindings: (orgId: string | null, agentId: string | null) =>
    ['mcp', 'agent-bindings', orgId, agentId] as const,
}

export function useMcpConnections(orgId: string | null, enabled = true) {
  return useQuery<McpConnection[]>({
    queryKey: mcpKeys.connections(orgId),
    queryFn: async () => {
      const res = await api.get<McpConnection[]>(
        `/orgs/${orgId}/mcp/connections`
      )
      return res.data
    },
    enabled: !!orgId && enabled,
    staleTime: 30_000,
  })
}

export function useMcpTools(
  orgId: string | null,
  connectionId: string | null,
  enabled = true
) {
  return useQuery<McpTool[]>({
    queryKey: mcpKeys.tools(orgId, connectionId),
    queryFn: async () => {
      const res = await api.get<McpTool[]>(
        `/orgs/${orgId}/mcp/connections/${connectionId}/tools`
      )
      return res.data
    },
    enabled: !!orgId && !!connectionId && enabled,
    staleTime: 30_000,
  })
}

export function useMcpToolsByConnections(
  orgId: string | null,
  connections: McpConnection[],
  enabled = true
) {
  const queries = useQueries({
    queries: connections.map((connection) => ({
      queryKey: mcpKeys.tools(orgId, connection.id),
      queryFn: async () => {
        const res = await api.get<McpTool[]>(
          `/orgs/${orgId}/mcp/connections/${connection.id}/tools`
        )
        return res.data
      },
      enabled: !!orgId && enabled,
      staleTime: 30_000,
    })),
  })

  return useMemo(() => {
    const byConnectionId: Record<string, McpTool[]> = {}
    connections.forEach((connection, index) => {
      byConnectionId[connection.id] = queries[index]?.data ?? []
    })
    return {
      byConnectionId,
      tools: Object.values(byConnectionId).flat(),
      isLoading: queries.some((query) => query.isLoading),
      isFetching: queries.some((query) => query.isFetching),
    }
  }, [connections, queries])
}

export function useMcpAgentBindings(
  orgId: string | null,
  agentId: string | null,
  enabled = true
) {
  return useQuery<McpAgentToolBinding[]>({
    queryKey: mcpKeys.agentBindings(orgId, agentId),
    queryFn: async () => {
      const res = await api.get<McpAgentToolBinding[]>(
        `/orgs/${orgId}/mcp/agents/${agentId}/tools`
      )
      return res.data
    },
    enabled: !!orgId && !!agentId && enabled,
    staleTime: 30_000,
  })
}

export function useCreateMcpConnection(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: McpConnectionInput) => {
      const res = await api.post<McpConnection>(
        `/orgs/${orgId}/mcp/connections`,
        data
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: mcpKeys.connections(orgId) })
    },
  })
}

export function useUpdateMcpConnection(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      connectionId,
      data,
    }: {
      connectionId: string
      data: McpConnectionUpdateInput
    }) => {
      const res = await api.patch<McpConnection>(
        `/orgs/${orgId}/mcp/connections/${connectionId}`,
        data
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: mcpKeys.connections(orgId) })
    },
  })
}

export function useRemoveMcpConnection(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (connectionId: string) => {
      await api.delete(`/orgs/${orgId}/mcp/connections/${connectionId}`)
      return connectionId
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: mcpKeys.connections(orgId) })
    },
  })
}

export function useSyncMcpTools(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (connectionId: string) => {
      const res = await api.post<McpTool[]>(
        `/orgs/${orgId}/mcp/connections/${connectionId}/sync-tools`
      )
      return { connectionId, tools: res.data }
    },
    onSuccess: ({ connectionId }) => {
      void qc.invalidateQueries({ queryKey: mcpKeys.connections(orgId) })
      void qc.invalidateQueries({
        queryKey: mcpKeys.tools(orgId, connectionId),
      })
    },
  })
}

export function useUpsertMcpAgentBinding(
  orgId: string | null,
  agentId: string | null
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      toolId,
      enabled,
      confirmation,
    }: {
      toolId: string
      enabled?: boolean
      confirmation?: McpToolConfirmation
    }) => {
      const res = await api.post<McpAgentToolBinding>(
        `/orgs/${orgId}/mcp/agents/${agentId}/tools/${toolId}`,
        { enabled, confirmation }
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: mcpKeys.agentBindings(orgId, agentId),
      })
      void qc.invalidateQueries({ queryKey: mcpKeys.connections(orgId) })
    },
  })
}
